import { Stack, Typography } from '@mui/material'

import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { LandReportBaseComponentProps } from '../types'
import { CarbonFootprintTable } from './CarbonFootprintTable'
import { CropChip, ExpansionPanel, FriendlyError, NotObservedChip, useFigures } from '@cibo/ui'
import { CarbonFootprintHeader } from './CarbonFootprintHeader'
import { CarbonFootprintSkeleton } from './CarbonFootprintSkeleton'
import { useFieldHistory } from '../../..'

export const CarbonFootprint: FC<LandReportBaseComponentProps> = ({
  children,
  field,
  focusYear,
}) => {
  const { t } = useTranslation('@cibo/landmanager/LandReport/CarbonFootprint')

  //@todo temporary while we wait for this information to be included on the field directly
  const fieldHistory = useFieldHistory(field.data?.resourceId)

  const { cropYield } = useFigures()
  const currentYearData = fieldHistory?.data?.historicalYields.find(
    ({ year }) => year === focusYear
  )

  if (field.isLoading || fieldHistory.isLoading) {
    return <CarbonFootprintSkeleton />
  }

  return (
    <Stack spacing={1} flex={1} p={2}>
      <ExpansionPanel
        spacing={1}
        header={<CarbonFootprintHeader />}
        collapsed={
          <Stack direction="row" spacing={3} alignItems="center">
            <Typography variant="subtitle1">{focusYear}</Typography>
            {!!fieldHistory.isError ? (
              <FriendlyError message={t('genericError')} />
            ) : (
              <>
                <Stack alignItems="center">
                  {currentYearData?.crop ? (
                    <CropChip crop={currentYearData?.crop} source="cibo" />
                  ) : (
                    <NotObservedChip />
                  )}
                  <Typography variant="caption">
                    {currentYearData?.crop
                      ? cropYield({ cropYield: currentYearData.yield, crop: currentYearData.crop })
                      : '--'}
                  </Typography>
                </Stack>
                <Stack>
                  <Typography variant="subtitle1">
                    {currentYearData?.ciboFootprint
                      ? t('emissionsTotal', {
                          amt:
                            currentYearData.ciboFootprint.results.total *
                            currentYearData.ciboFootprint.results.acreage,
                        })
                      : '--'}
                  </Typography>
                  <Typography variant="caption">
                    {currentYearData?.ciboFootprint
                      ? t('emissionsPerAcre', {
                          amt: currentYearData.ciboFootprint.results.total,
                        })
                      : '--'}
                  </Typography>
                </Stack>
              </>
            )}
          </Stack>
        }
        expanded={
          <CarbonFootprintTable field={field} fieldHistory={fieldHistory} focusYear={focusYear} />
        }
        disabled={fieldHistory.isError}
      />
      {children}
    </Stack>
  )
}
